import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { translations } from "@/i18n/translations";

const LanguageContext = createContext(null);

const resolve = (obj, path) =>
  path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "fr";
    return localStorage.getItem("ml-lang") || "fr";
  });

  const setLang = useCallback((l) => {
    setLangState(l);
    try {
      localStorage.setItem("ml-lang", l);
      document.documentElement.lang = l;
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[i18n] Failed to persist language preference:", e);
      }
    }
  }, []);

  const toggleLang = useCallback(() => {
    setLang(lang === "fr" ? "en" : "fr");
  }, [lang, setLang]);

  const t = useCallback(
    (path) => {
      const val = resolve(translations[lang], path);
      return val === undefined ? resolve(translations.fr, path) : val;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, toggleLang, t }), [lang, setLang, toggleLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
};
